import { type ItineraryListItem, type ItineraryListQuery } from '@utazzo/shared';
import { UzBadge, UzConfirmDialog, UzStatusBadge } from '@utazzo/ui';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  Power,
  PowerOff,
  Receipt,
  Trash2,
} from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ItineraryPricingDialog } from '../components/ItineraryPricingDialog';
import {
  useDeleteItinerary,
  useItinerariesList,
  useItineraryVersions,
  useSetItineraryActive,
} from '../use-itineraries';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { useColumnWidths } from '@/components/data-grid/use-column-widths';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

/**
 * Itineraries list (Phase B5.A, columns redesigned 2026-07-09). 21k
 * legacy + new itineraries visible in the admin.
 *
 * The 2026-07-09 redesign brings the column shape in line with the
 * legacy PHP screen: Actions moved to column 2, Quote ID + share
 * link, per-column customer / travel / headcount / hotel columns.
 * Group tinting is done via `className` on each column (no colspan
 * header - the DataGrid doesn't render two-row headers). Actions
 * only render for the LATEST major version so revisions carry no
 * delete affordances.
 *
 * Filters: status, kind, revisions-visibility, archived. Search is
 * server-side across title + legacy AG-ID via the shared `q` param.
 */

/** Filter values the dashboard opens on. Module-level so the reference stays
 *  stable across renders (it seeds the URL-state parser defaults). The
 *  dashboard opens unfiltered (2026-10-24; it used to open on the `live`
 *  preset, which hid in-progress quotes agents were looking for). `live` is
 *  still a pick: it expands server-side to SENT_TO_CUSTOMER + ACCEPTED via
 *  `statusIn` on the list query. */
const DEFAULT_FILTERS: Readonly<Record<string, string>> = {};

// DRAFT retired - initial + revised itineraries all start as IN_PROGRESS.
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'live', label: 'Live (sent + accepted)' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'SENT_TO_CUSTOMER', label: 'Sent to customer' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CONVERTED_TO_BOOKING', label: 'Converted to booking' },
  { value: 'CANCELLED', label: 'Cancelled' },
] as const;

const KIND_OPTIONS = [
  { value: '', label: 'All kinds' },
  { value: 'CUSTOM', label: 'FIT' },
  { value: 'BRIEF', label: 'Brief' },
  { value: 'HOLIDAY', label: 'Holiday' },
  { value: 'FIXED_DEPARTURE', label: 'Fixed departure' },
] as const;

// 2026-07-17. Default is "Latest only" (one row per chain) and each row shows
// a chevron that expands to reveal older versions inline. Operators asked for
// the master/child pattern so the list stays scannable while the drill-down
// path to a specific historical version is one click deep. "All versions"
// keeps the flat behaviour for anyone who prefers the older layout.
const REVISIONS_OPTIONS = [
  { value: '', label: 'Latest only' },
  { value: 'all', label: 'All versions' },
] as const;

const ARCHIVED_OPTIONS = [
  { value: '', label: 'Active only' },
  { value: 'archived', label: 'Include archived' },
] as const;

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'kind',
    label: 'Kind',
    options: KIND_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
  {
    id: 'revisions',
    label: 'Revisions',
    options: REVISIONS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'archived',
    label: 'Archived',
    options: ARCHIVED_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
];

const KIND_LABEL: Record<ItineraryListItem['kind'], string> = {
  // FIT = Flexible Itinerary. Enum value stays CUSTOM; only the label changed.
  CUSTOM: 'FIT',
  BRIEF: 'Brief',
  HOLIDAY: 'Holiday',
  FIXED_DEPARTURE: 'Fixed dep.',
};

const mapStatus = (s: ItineraryListItem['status']): 'active' | 'inactive' | 'draft' | 'pending' => {
  switch (s) {
    case 'DRAFT':
      return 'draft';
    case 'IN_PROGRESS':
    case 'REVISED':
      return 'pending';
    case 'SENT_TO_CUSTOMER':
    case 'ACCEPTED':
    case 'CONVERTED_TO_BOOKING':
      return 'active';
    default:
      return 'inactive';
  }
};

const STATUS_LABEL: Record<ItineraryListItem['status'], string> = {
  DRAFT: 'Draft',
  IN_PROGRESS: 'In progress',
  SENT_TO_CUSTOMER: 'Sent to customer',
  REVISED: 'Revised',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  CONVERTED_TO_BOOKING: 'Converted to booking',
  CANCELLED: 'Cancelled',
};

const formatPaise = (paise: string, currencyHint: string | null): string => {
  const n = Number(paise);
  if (!Number.isFinite(n) || n === 0) return '-';
  const prefix = currencyHint ? `${currencyHint} ` : '₹';
  return `${prefix}${(n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatShortDate = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Column-group tint classes. Applied via `className` on each column
 * so the alternating pastel bands communicate the same grouping the
 * reference screenshot uses (Identity | People | Timeline | Trip |
 * Headcount | Room | Money). Values kept subtle so the row-hover +
 * status badge stay dominant.
 */
const GROUP_TINT = {
  PEOPLE: 'bg-sky-50/50',
  TIMELINE: 'bg-zinc-50/60',
  TRIP: 'bg-emerald-50/40',
  HEADCOUNT: 'bg-amber-50/50',
  ROOM: 'bg-violet-50/40',
  MONEY: 'bg-teal-50/50',
} as const;

/** Small `v{n}` chip shown on every row (master + child) so each row states
 *  its version. Same visual on both tables, per the master/child parity ask. */
const VersionChip = ({ version }: { version: number }): React.JSX.Element => (
  <span className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-zinc-700 tabular-nums">
    v{version}
  </span>
);

/** Grid-only leading column widths (not part of DESCRIPTIVE_COLUMNS). Shared so
 *  the child table's colgroup matches the master's leading columns exactly. */
const EXPANDER_WIDTH = '36px';
const SERIAL_WIDTH = '64px';
const ACTIONS_WIDTH = '148px';

type RowActionHandlers = {
  canDelete: boolean;
  onPricing: (id: string) => void;
  onToggle: (row: ItineraryListItem, active: boolean) => void;
  onDelete: (row: ItineraryListItem) => void;
};

/**
 * Per-row action icons (pricing / share / activate-deactivate / delete),
 * shared by the master rows AND every child version row so each version carries
 * the same affordances. Each button stops propagation so it never triggers the
 * row-click detail navigation. Which icons show is driven by status /
 * permission, NOT by whether the row is the latest revision - past versions get
 * actions too.
 */
const RowActionsCell = ({
  row,
  h,
}: {
  row: ItineraryListItem;
  h: RowActionHandlers;
}): React.JSX.Element => {
  const stop = (e: React.MouseEvent): void => {
    e.stopPropagation();
  };
  const canShare = row.status === 'SENT_TO_CUSTOMER' && Boolean(row.shareUrl);
  const isCancelled = row.status === 'CANCELLED';
  const canDeactivate =
    !row.leadIsWon &&
    (row.status === 'SENT_TO_CUSTOMER' || row.status === 'ACCEPTED' || row.status === 'REVISED');
  const canActivate = isCancelled && !row.leadIsWon;
  const canToggleActive = canActivate || canDeactivate;
  return (
    <div className="flex items-center gap-1">
      {h.canDelete && (
        <button
          type="button"
          title="View pricing breakup"
          aria-label="View pricing breakup"
          onClick={(e) => {
            stop(e);
            h.onPricing(row.id);
          }}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-sky-600 transition-colors hover:bg-sky-50 focus-visible:ring-2 focus-visible:ring-sky-200 focus-visible:outline-none"
        >
          <Receipt className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      {canShare && (
        <button
          type="button"
          title="Open share link"
          aria-label="Open customer share link"
          onClick={(e) => {
            stop(e);
            window.open(row.shareUrl!, '_blank', 'noopener');
          }}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-emerald-600 transition-colors hover:bg-emerald-50 focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:outline-none"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      {canToggleActive && (
        <button
          type="button"
          title={isCancelled ? 'Activate itinerary' : 'Deactivate itinerary'}
          aria-label={isCancelled ? 'Activate itinerary' : 'Deactivate itinerary'}
          onClick={(e) => {
            stop(e);
            h.onToggle(row, isCancelled);
          }}
          className={
            isCancelled
              ? 'inline-flex h-7 w-7 items-center justify-center rounded-md text-emerald-600 transition-colors hover:bg-emerald-50 focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:outline-none'
              : 'inline-flex h-7 w-7 items-center justify-center rounded-md text-amber-600 transition-colors hover:bg-amber-50 focus-visible:ring-2 focus-visible:ring-amber-200 focus-visible:outline-none'
          }
        >
          {isCancelled ? (
            <Power className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <PowerOff className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
      )}
      {h.canDelete && (
        <button
          type="button"
          title="Delete itinerary"
          aria-label="Delete itinerary"
          onClick={(e) => {
            stop(e);
            h.onDelete(row);
          }}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-rose-600 transition-colors hover:bg-rose-50 focus-visible:ring-2 focus-visible:ring-rose-200 focus-visible:outline-none"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
};

/**
 * The descriptive columns shared by BOTH the master rows and the expanded
 * child-version rows, so the child table renders with identical columns / cell
 * design. Excludes the leading grid-only columns (expander / S.No / Actions),
 * which are master-only. Every cell is a pure function of the row, so a merged
 * "version row" (parent shared fields + the version's own totals / status /
 * quote) renders through the exact same cells.
 */
const DESCRIPTIVE_COLUMNS: ReadonlyArray<DataGridColumn<ItineraryListItem>> = [
  {
    id: 'title',
    header: 'Itinerary',
    sortable: true,
    width: '260px',
    cell: (row) => {
      const titleHasAgId = Boolean(row.legacyAGId && row.title.includes(row.legacyAGId));
      return (
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-zinc-900">{row.title}</span>
            <VersionChip version={row.revisionNumber} />
          </div>
          {(Boolean(row.legacyAGId && !titleHasAgId) || Boolean(row.leadReference)) && (
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              {row.legacyAGId && !titleHasAgId && <span>{row.legacyAGId}</span>}
              {row.leadReference && (
                <>
                  {row.legacyAGId && !titleHasAgId && <span className="text-zinc-300">·</span>}
                  <span>Lead {row.leadReference}</span>
                </>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    id: 'quote',
    header: 'Quote No.',
    width: '150px',
    truncate: false,
    className: GROUP_TINT.MONEY,
    cell: (row) =>
      row.quoteId ? (
        <span className="font-mono text-xs whitespace-nowrap text-zinc-700">{row.quoteId}</span>
      ) : row.quoteNo !== null ? (
        <span className="font-mono text-xs text-zinc-700 tabular-nums">#{row.quoteNo}</span>
      ) : (
        <span className="text-zinc-400">-</span>
      ),
  },
  {
    id: 'customer',
    header: 'Customer',
    width: '200px',
    className: GROUP_TINT.PEOPLE,
    cell: (row) => (
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm text-zinc-900">
          {row.customerName ?? <span className="text-zinc-400">-</span>}
        </span>
        {row.assignedAgentName && (
          <span className="truncate text-xs text-zinc-500">{row.assignedAgentName}</span>
        )}
      </div>
    ),
  },
  {
    id: 'kind',
    header: 'Kind',
    width: '110px',
    // Fixed departures get their own tone: legacy distinguished them with a
    // separate reference prefix (UTZF vs UTZI), which we deliberately did not
    // copy (ADR-0069 D10). The pill is what makes them scannable instead.
    cell: (row) => (
      <UzBadge tone={row.kind === 'FIXED_DEPARTURE' ? 'warning' : 'secondary'}>
        {KIND_LABEL[row.kind]}
      </UzBadge>
    ),
  },
  {
    id: 'travelStartDate',
    header: 'Travel Date',
    sortable: true,
    width: '130px',
    truncate: false,
    className: GROUP_TINT.TRIP,
    cell: (row) => (
      <span className="text-sm whitespace-nowrap text-zinc-700">
        {formatShortDate(row.travelStartDate)}
      </span>
    ),
  },
  {
    id: 'grandTotal',
    header: 'Total',
    width: '160px',
    align: 'right',
    className: GROUP_TINT.MONEY,
    cell: (row) => (
      <span className="font-mono text-sm font-medium whitespace-nowrap text-zinc-900 tabular-nums">
        {formatPaise(row.grandTotalPaise, row.currencyCode)}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    width: '150px',
    cell: (row) => (
      <UzStatusBadge status={mapStatus(row.status)} label={STATUS_LABEL[row.status]} />
    ),
  },
  {
    id: 'destination',
    header: 'Destination',
    width: '150px',
    truncate: false,
    className: GROUP_TINT.TRIP,
    cell: (row) =>
      row.destinationName ? (
        <span className="text-sm whitespace-nowrap text-zinc-900">{row.destinationName}</span>
      ) : (
        <span className="text-zinc-400">-</span>
      ),
  },
  {
    id: 'nights',
    header: 'Nights',
    width: '80px',
    align: 'right',
    className: GROUP_TINT.TRIP,
    cell: (row) =>
      row.numberOfNights > 0 ? (
        <span className="font-mono text-sm text-zinc-800 tabular-nums">{row.numberOfNights}</span>
      ) : (
        <span className="text-zinc-400">-</span>
      ),
  },
  {
    id: 'customerEmail',
    header: 'Customer Email',
    width: '220px',
    className: GROUP_TINT.PEOPLE,
    cell: (row) =>
      row.customerEmail ? (
        <span className="truncate text-sm text-zinc-700">{row.customerEmail}</span>
      ) : (
        <span className="text-zinc-400">-</span>
      ),
  },
  {
    id: 'customerPhone',
    header: 'Customer Contact',
    width: '160px',
    truncate: false,
    className: GROUP_TINT.PEOPLE,
    cell: (row) =>
      row.customerPhone ? (
        <span className="font-mono text-xs whitespace-nowrap text-zinc-700">
          {row.customerPhone}
        </span>
      ) : (
        <span className="text-zinc-400">-</span>
      ),
  },
  {
    id: 'adults',
    header: 'Adults',
    width: '72px',
    align: 'right',
    className: GROUP_TINT.HEADCOUNT,
    cell: (row) => (
      <span className="font-mono text-sm text-zinc-800 tabular-nums">{row.adultsCount || '-'}</span>
    ),
  },
  {
    id: 'children',
    header: 'Child',
    width: '72px',
    align: 'right',
    className: GROUP_TINT.HEADCOUNT,
    cell: (row) => (
      <span className="font-mono text-sm text-zinc-800 tabular-nums">
        {row.childrenCount || '-'}
      </span>
    ),
  },
  {
    id: 'infants',
    header: 'Infant',
    width: '72px',
    align: 'right',
    className: GROUP_TINT.HEADCOUNT,
    cell: (row) => (
      <span className="font-mono text-sm text-zinc-800 tabular-nums">
        {row.infantsCount || '-'}
      </span>
    ),
  },
  {
    id: 'hotelName',
    header: 'Hotel Name',
    width: '220px',
    className: GROUP_TINT.ROOM,
    cell: (row) =>
      row.hotelName ? (
        <span className="truncate text-sm text-zinc-900">{row.hotelName}</span>
      ) : (
        <span className="text-zinc-400">-</span>
      ),
  },
  {
    id: 'rooms',
    header: 'Rooms',
    width: '80px',
    align: 'right',
    className: GROUP_TINT.ROOM,
    cell: (row) =>
      row.roomsCount > 0 ? (
        <span className="font-mono text-sm text-zinc-800 tabular-nums">{row.roomsCount}</span>
      ) : (
        <span className="text-zinc-400">-</span>
      ),
  },
  {
    id: 'createdBy',
    header: 'Created By',
    width: '140px',
    truncate: false,
    className: GROUP_TINT.TIMELINE,
    cell: (row) =>
      row.createdByName ? (
        <span className="text-sm whitespace-nowrap text-zinc-800">{row.createdByName}</span>
      ) : (
        <span className="text-zinc-400">-</span>
      ),
  },
  {
    id: 'createdAt',
    header: 'Created On',
    sortable: true,
    width: '170px',
    truncate: false,
    className: GROUP_TINT.TIMELINE,
    cell: (row) => (
      <span className="text-sm whitespace-nowrap text-zinc-700">
        {formatDateTime(row.createdAt)}
      </span>
    ),
  },
  {
    id: 'updatedBy',
    header: 'Updated By',
    width: '140px',
    truncate: false,
    className: GROUP_TINT.TIMELINE,
    cell: (row) =>
      row.updatedByName ? (
        <span className="text-sm whitespace-nowrap text-zinc-800">{row.updatedByName}</span>
      ) : (
        <span className="text-zinc-400">-</span>
      ),
  },
  {
    id: 'updatedAt',
    header: 'Updated On',
    sortable: true,
    width: '170px',
    truncate: false,
    className: GROUP_TINT.TIMELINE,
    cell: (row) => (
      <span className="text-sm whitespace-nowrap text-zinc-700">
        {formatDateTime(row.updatedAt)}
      </span>
    ),
  },
  {
    id: 'publishedAt',
    header: 'Published On',
    width: '170px',
    truncate: false,
    className: GROUP_TINT.TIMELINE,
    cell: (row) =>
      row.publishedAt ? (
        <span className="text-sm whitespace-nowrap text-zinc-700">
          {formatDateTime(row.publishedAt)}
        </span>
      ) : (
        <span className="text-zinc-400">-</span>
      ),
  },
];

export const ItinerariesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteItinerary = useDeleteItinerary();
  const setActive = useSetItineraryActive();
  const navigate = useNavigate();
  const [pendingDelete, setPendingDelete] = useState<ItineraryListItem | null>(null);
  const [pendingToggle, setPendingToggle] = useState<{
    row: ItineraryListItem;
    active: boolean;
  } | null>(null);
  // 2026-07-09. Admin-only Pricing breakup dialog. Kept as state on the
  // list page so navigating between rows opens the same modal instance
  // and React Query caches the detail fetch per id.
  const [pricingItineraryId, setPricingItineraryId] = useState<string | null>(null);
  // 2026-07-17. Master/child expansion state - the id of each latest-row
  // whose older versions the operator has expanded inline. Fetched via
  // useItineraryVersions inside ExpandedVersionsRow when the row mounts.
  // 2026-10-10. Older versions are now expanded by DEFAULT for everyone. The
  // state tracks the ids the operator has explicitly COLLAPSED (empty = all open),
  // so a fresh/paginated row shows its versions without a click; toggling a row
  // just adds/removes it from the collapsed set.
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(() => new Set());
  const toggleExpanded = (id: string): void =>
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Shared with the child version rows so every version renders the same
  // action icons wired to the same handlers.
  const actionHandlers: RowActionHandlers = {
    canDelete,
    onPricing: setPricingItineraryId,
    onToggle: (row, active) => setPendingToggle({ row, active }),
    onDelete: setPendingDelete,
  };

  const columns: ListScreenColumns<ItineraryListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<ItineraryListItem>> = [
      // Chevron toggle for master/child expansion. Only rendered on rows with
      // predecessors (`revisionNumber > 1`) - v1 has nothing to drill into so
      // the cell stays visually blank and the row still clicks through to
      // detail as usual.
      {
        id: 'expander',
        header: '',
        width: '36px',
        alwaysVisible: true,
        truncate: false,
        cell: (row) => {
          if (row.revisionNumber <= 1) return <span aria-hidden />;
          const isOpen = !collapsedIds.has(row.id);
          return (
            <button
              type="button"
              aria-label={isOpen ? 'Collapse older versions' : 'Show older versions'}
              aria-expanded={isOpen}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(row.id);
              }}
              className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
            >
              {isOpen ? (
                <ChevronDown className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronRight className="h-4 w-4" aria-hidden />
              )}
            </button>
          );
        },
      },
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      // Actions - shared cell rendered on EVERY version (master + child).
      // Icons are gated by status / permission, not by latest-vs-past, so past
      // versions carry the same affordances. Row click still opens the detail.
      {
        id: 'actions',
        header: 'Actions',
        width: ACTIONS_WIDTH,
        alwaysVisible: true,
        truncate: false,
        cell: (row) => <RowActionsCell row={row} h={actionHandlers} />,
      },
      ...DESCRIPTIVE_COLUMNS,
    ];
    return cols;
  };

  return (
    <>
      <ListScreen<ItineraryListItem, ItineraryListQuery>
        title="Itinerary Dashboard"
        description="Customer itineraries."
        // Opens on the "Sent to customer" slice - the queue operators work
        // day-to-day. Clearing/changing the filter behaves as before.
        defaultFilters={DEFAULT_FILTERS}
        useList={useItinerariesList}
        defaultSort={{ by: 'createdAt', dir: 'desc' }}
        minSearchLength={MIN_SEARCH_LENGTH}
        // 2026-07-09. Auto layout so the table respects each column's
        // explicit width and the outer container scrolls horizontally
        // when the sum overflows the viewport. Prevents the header
        // labels from collapsing to a single character on narrow
        // screens.
        tableLayout="fixed"
        toQuery={(state, base) => {
          const status = state.filters.status;
          const kind = state.filters.kind;
          const revisions = state.filters.revisions;
          const archived = state.filters.archived;
          // `live` preset expands to two statuses via `statusIn`. Any other
          // single-status pick uses the plain `status` param.
          const statusFilter =
            status === 'live'
              ? {
                  statusIn:
                    'SENT_TO_CUSTOMER,ACCEPTED' as unknown as ItineraryListQuery['statusIn'],
                }
              : status
                ? { status: status as ItineraryListQuery['status'] }
                : {};
          return {
            ...base,
            ...statusFilter,
            ...(kind ? { kind: kind as ItineraryListQuery['kind'] } : {}),
            // Default (revisions === '') shows LATEST only - operator drills
            // into older versions via the row's chevron. Explicit "All" shows
            // every revision flat, same as the old behaviour.
            ...(revisions === 'all'
              ? { onlyLatestRevisions: false }
              : { onlyLatestRevisions: true }),
            ...(archived === 'archived' ? { includeDeleted: true } : {}),
          };
        }}
        columns={columns}
        filters={FILTERS}
        getRowId={(row) => row.id}
        onRowClick={(row) => navigate(`/itineraries/${row.id}`)}
        renderRowExtras={(row) => {
          // Only multi-version rows have older versions to show; everything is
          // expanded by default (shown) unless the operator collapsed this row.
          if (row.revisionNumber <= 1 || collapsedIds.has(row.id) || !row.leadId) return null;
          return (
            <ExpandedVersionsRow
              parentRow={row}
              actionHandlers={actionHandlers}
              onOpen={(itineraryId) =>
                navigate(`/itineraries/${itineraryId}`, {
                  state: { freshVersion: true },
                })
              }
            />
          );
        }}
        copy={{
          searchPlaceholder: 'Search title or AG-ID (min 3 chars)',
          emptyTitle: 'No itineraries match these filters',
          emptyDescription: 'Adjust filters or clear the search box.',
          errorTitle: 'Failed to load itineraries',
          errorDescription: 'The server returned an error. Try refreshing or check your network.',
        }}
      />
      <UzConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="Delete this itinerary?"
        description={
          pendingDelete
            ? `${pendingDelete.title} will be soft-deleted and hidden from lists. Revisions and quotes are preserved for audit.`
            : ''
        }
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={async () => {
          if (!pendingDelete) return;
          await deleteItinerary.mutateAsync(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
      <UzConfirmDialog
        open={pendingToggle !== null}
        onOpenChange={(open) => {
          if (!open) setPendingToggle(null);
        }}
        title={
          pendingToggle
            ? pendingToggle.active
              ? 'Reactivate this itinerary?'
              : 'Deactivate this itinerary?'
            : ''
        }
        description={
          pendingToggle
            ? pendingToggle.active
              ? `${pendingToggle.row.title} will move back to Sent to customer. The share URL will resolve again and the PayU link (if there was one) will be restored.`
              : `${pendingToggle.row.title} will move to Cancelled. The customer share URL will start returning 404, the PayU payment link goes dead, and the row drops out of the version-picker dropdown. You can reactivate any time.`
            : ''
        }
        confirmLabel={pendingToggle?.active ? 'Reactivate' : 'Deactivate'}
        tone={pendingToggle?.active ? 'primary' : 'destructive'}
        onConfirm={async () => {
          if (!pendingToggle) return;
          await setActive.mutateAsync({
            id: pendingToggle.row.id,
            active: pendingToggle.active,
          });
          setPendingToggle(null);
        }}
      />
      {/* Admin-only pricing breakup popup. Rendered here so the modal's
          React Query fetches survive Actions dropdown close (the dropdown
          unmounts on select). */}
      <ItineraryPricingDialog
        open={pricingItineraryId !== null}
        onOpenChange={(open) => {
          if (!open) setPricingItineraryId(null);
        }}
        itineraryId={pricingItineraryId}
      />
    </>
  );
};

/**
 * Renders the older versions of an expanded itinerary chain as a nested table
 * that mirrors the master table's columns exactly (same DESCRIPTIVE_COLUMNS
 * cells + widths), so the child reads as a continuation of the master. Fetches
 * on mount via `useItineraryVersions(leadId)` - React Query dedupes across
 * siblings on the same lead. Each row navigates to that revision with
 * `state.freshVersion=true` so ItineraryDetailPage's "redirect to latest"
 * effect skips.
 *
 * The parent (latest) row is filtered out so only historical versions render
 * here - it is NOT repeated; v(n-1) -> ... -> v1, newest first. Each version's
 * chain-wide fields (customer / destination / travel / pax) are inherited from
 * the parent row; only the per-version fields (status / total / quote / version
 * number) are overridden, so every column shows the right value.
 */
const CHILD_CELL_CLASS =
  'border-b border-[rgb(228_228_231_/_0.55)] px-3.5 py-2.5 align-top text-[15px] text-[var(--fg)]';

const ExpandedVersionsRow = ({
  parentRow,
  actionHandlers,
  onOpen,
}: {
  parentRow: ItineraryListItem;
  actionHandlers: RowActionHandlers;
  onOpen: (itineraryId: string) => void;
}): React.JSX.Element => {
  const versionsQuery = useItineraryVersions(parentRow.leadId);
  const versions = (versionsQuery.data ?? [])
    .filter((v) => v.itineraryId !== parentRow.id)
    .sort((a, b) => b.version - a.version);
  // Read the SAME persisted widths the master DataGrid uses (keyed by pathname),
  // so the child colgroup matches the master's live (possibly resized) columns
  // exactly - not just the static defaults. Guarantees column-for-column align.
  const { widths } = useColumnWidths(
    typeof window !== 'undefined' ? window.location.pathname : 'datagrid',
  );
  const colWidth = (id: string, fallback: string | number): number | string =>
    widths[id] ?? fallback;

  if (versionsQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-xs text-zinc-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Loading older versions...
      </div>
    );
  }
  if (versions.length === 0) {
    return (
      <div className="px-4 py-3 text-xs text-zinc-500">
        No older versions found for this itinerary.
      </div>
    );
  }
  // The child mirrors the master's full column layout (leading expander / S.No /
  // Actions + descriptive columns) at identical widths, and the master runs in
  // `fixed` layout, so cells line up column-for-column.
  return (
    <div className="bg-zinc-50/30">
      <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: colWidth('expander', EXPANDER_WIDTH) }} />
          <col style={{ width: colWidth('serial', SERIAL_WIDTH) }} />
          <col style={{ width: colWidth('actions', ACTIONS_WIDTH) }} />
          {DESCRIPTIVE_COLUMNS.map((c) => (
            <col key={c.id} style={{ width: colWidth(c.id, c.width ?? 'auto') }} />
          ))}
        </colgroup>
        <tbody>
          {versions.map((v) => {
            const mergedRow: ItineraryListItem = {
              ...parentRow,
              id: v.itineraryId,
              status: v.status,
              grandTotalPaise: v.grandTotalPaise,
              quoteId: v.quoteId,
              quoteNo: v.quoteNo,
              publishedAt: v.publishedAt,
              shareUrl: v.shareUrl,
              isLatestRevision: false,
              revisionNumber: v.version,
            };
            return (
              <tr
                key={v.itineraryId}
                onClick={() => onOpen(v.itineraryId)}
                className="cursor-pointer transition-colors hover:bg-white last:[&>td]:border-b-0"
              >
                {/* Expander + S.No are master-only affordances; blank on the
                    child but kept as real columns so widths line up. */}
                <td className={CHILD_CELL_CLASS} aria-hidden />
                <td className={CHILD_CELL_CLASS} aria-hidden />
                <td
                  className={`${CHILD_CELL_CLASS} overflow-visible`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <RowActionsCell row={mergedRow} h={actionHandlers} />
                </td>
                {DESCRIPTIVE_COLUMNS.map((col) => {
                  const alignClass =
                    col.align === 'right'
                      ? 'text-right tabular-nums'
                      : col.align === 'center'
                        ? 'text-center'
                        : '';
                  return (
                    <td
                      key={col.id}
                      className={`${CHILD_CELL_CLASS} ${
                        col.truncate === false
                          ? 'overflow-visible'
                          : 'wrap-break-word whitespace-normal'
                      } ${alignClass} ${col.className ?? ''}`}
                    >
                      {col.cell(mergedRow, 0)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
